import WsClient from "../ws/WsClient"
import { API_URL } from "../config"
import type { UserLanguagePreferences } from "@inpriton/types/conferenceTypes"

// RISKS.md/P1.20: one place to POST /conference/preferences, because both call sites (the green
// room's CheckIn and the sidebar's settings panel) previously did `await res.json()` with NO
// res.ok check — an HTTP 401/403/500 carrying a JSON body was indistinguishable from success.
// A rejected save was therefore silently lost: no toast, no console error, the spinner cleared
// normally, and the participant lost interpreter assignment for the whole meeting.

export type PreferencesBody = {
  spokenLanguage: string | null
  neededLanguages: string[]
  simultanConfig?: number
}

export type PreferencesResponse = {
  success?: boolean
} & Partial<UserLanguagePreferences>

// The saved languages alone, onto what the status holds: the routing arrives in pushes, and a
// waiting user's save reply, which names no one, can land after the `admitted` push that does
// (RISKS.md/P1.28).
export const withSavedLanguages = (
  held: UserLanguagePreferences | null,
  languages: Pick<UserLanguagePreferences, 'spokenLanguage' | 'neededLanguages' | 'simultanConfig'>,
): UserLanguagePreferences => ({
  assignedTranslatorUuids: [],
  responsibleTranslatorUuids: [],
  missingInterpreters: [],
  awaitingInterpreters: [],
  speakersToFilter: [],
  ...held,
  ...languages,
})

export class PreferencesSaveError extends Error {
  // HTTP status of the failed response, or undefined for a network/parse failure.
  public status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'PreferencesSaveError'
    this.status = status
  }
}

const post = (token: string, body: PreferencesBody) =>
  fetch(`${API_URL}/conference/preferences`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

/**
 * Save the user's language/interpreter preferences.
 *
 * Throws PreferencesSaveError on any non-2xx response or transport failure, so callers must
 * handle failure explicitly instead of writing a fabricated "saved" state into the UI.
 * On a 401 the access token is refreshed once (RISKS.md/P1.20 — a save racing the silent
 * refresh must not cost the user their settings) and the POST retried on the new token.
 */
export const savePreferences = async (ws: WsClient, body: PreferencesBody): Promise<PreferencesResponse> => {
  if (!ws.token) {
    throw new PreferencesSaveError('not authenticated')
  }

  let res: Response
  try {
    res = await post(ws.token, body)
    if (res.status === 401 && await ws.refreshNow() && ws.token) {
      res = await post(ws.token, body)
    }
  } catch (e: any) {
    throw new PreferencesSaveError(e?.message ?? 'network error')
  }

  if (!res.ok) {
    throw new PreferencesSaveError(`preferences save failed (${res.status})`, res.status)
  }

  try {
    return await res.json() as PreferencesResponse
  } catch (e: any) {
    throw new PreferencesSaveError(e?.message ?? 'invalid response body', res.status)
  }
}
